import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import type { ConversationsTableFilters } from "../types"
import type { FilterState } from "../types/filterState"
import type { ConversationsMetricLabelSourcesById } from "../types/metricAdapters"

interface ConversationsTableColumn {
  key: string
  label: string
  fullLabel?: string
  width?: string
}

interface BuildConversationsTableColumnsParams {
  allConversationsData: ConversationsTableResult[]
  allColumns: ConversationsTableColumn[]
  defaultConversationMetrics: string[]
  metricLabelsById: ConversationsMetricLabelSourcesById
  // Metric IDs present in the fetched metric catalog. Metric keys from row data
  // or org defaults that aren't in this set are dropped — an empty set (e.g.
  // while records are still loading) hides all metric columns.
  knownMetricIds: Set<string>
}

export function buildConversationsTableColumns({
  allConversationsData,
  allColumns,
  defaultConversationMetrics,
  metricLabelsById,
  knownMetricIds,
}: BuildConversationsTableColumnsParams): ConversationsTableColumn[] {
  const metricKeys = new Set<string>()

  allConversationsData.forEach((item) => {
    Object.keys(item.metrics || {}).forEach((key) => {
      if (knownMetricIds.has(key)) {
        metricKeys.add(key)
      }
    })
  })

  defaultConversationMetrics.forEach((metric) => {
    if (knownMetricIds.has(metric)) {
      metricKeys.add(metric)
    }
  })

  const metricColumns = Array.from(metricKeys)
    .sort((left, right) => {
      const leftName = resolveMetricDisplayName(
        metricLabelsById[left]?.metric_display_name,
        left,
      )
      const rightName = resolveMetricDisplayName(
        metricLabelsById[right]?.metric_display_name,
        right,
      )

      return leftName.localeCompare(rightName)
    })
    .map((metricKey) => {
      const displayName = resolveMetricDisplayName(
        metricLabelsById[metricKey]?.metric_display_name,
        metricKey,
      )

      return {
        key: `metric_${metricKey}`,
        label: displayName,
        fullLabel: displayName,
        width: undefined,
      }
    })

  // Tags and ID close the table, in that order, however many metric columns
  // are visible — so they come out of the fixed set first and
  // go back on at the very end.
  const trailingKeys = ["tags", "simulation_output_id"]
  const trailingColumns = trailingKeys
    .map((key) => allColumns.find((column) => column.key === key))
    .filter(
      (column): column is ConversationsTableColumn => column !== undefined,
    )
  const leadingColumns = allColumns.filter(
    (column) => !trailingKeys.includes(column.key),
  )

  // Metric columns are inserted just before the "Metadata" column, so Metadata
  // renders to the right of the metrics block. Falls back to appending metrics
  // after the fixed columns when there is no Metadata column.
  const metadataIndex = leadingColumns.findIndex(
    (column) => column.key === "customer_metadata",
  )
  const metricsInsertAt =
    metadataIndex === -1 ? leadingColumns.length : metadataIndex

  return [
    ...leadingColumns.slice(0, metricsInsertAt),
    ...metricColumns,
    ...leadingColumns.slice(metricsInsertAt),
    ...trailingColumns,
  ]
}

export function buildConversationsFiltersSnapshot({
  agentIds,
  dateRange,
  metadataFilters,
  metricIds,
  metricValueFilters,
  tagFilters,
  projectIds,
}: Pick<
  FilterState,
  | "agentIds"
  | "dateRange"
  | "metadataFilters"
  | "metricIds"
  | "metricValueFilters"
  | "tagFilters"
  | "projectIds"
>): ConversationsTableFilters {
  return {
    startDate: dateRange.start?.toISOString(),
    endDate: dateRange.end?.toISOString(),
    metricIds: metricIds.length > 0 ? metricIds : undefined,
    agentIds: agentIds.length > 0 ? agentIds : undefined,
    tagFilters: tagFilters.length > 0 ? tagFilters : undefined,
    projectIds: projectIds.length > 0 ? projectIds : undefined,
    metadataFilters: metadataFilters.length > 0 ? metadataFilters : undefined,
    metricValueFilters:
      metricValueFilters.length > 0 ? metricValueFilters : undefined,
  }
}

export function getConversationsSimulationOutputIds(
  rows: ConversationsTableResult[],
): string[] {
  return rows
    .map((row) => row.simulation_output_id)
    .filter((simulationOutputId): simulationOutputId is string =>
      Boolean(simulationOutputId),
    )
}

export function getConversationsMetricIds(
  rows: ConversationsTableResult[],
  extraMetricIds: string[] = [],
): string[] {
  const metricIds = new Set<string>()

  for (const row of rows) {
    for (const metricId of Object.keys(row.metrics ?? {})) {
      metricIds.add(metricId)
    }

    for (const metricId of Object.keys(row.metric_error_statuses ?? {})) {
      metricIds.add(metricId)
    }
  }

  for (const metricId of extraMetricIds) {
    if (metricId.trim().length > 0) {
      metricIds.add(metricId)
    }
  }

  return Array.from(metricIds).sort()
}

export function getSelectedConversationsRunIds(
  rows: ConversationsTableResult[],
  selectedKeys: Set<string>,
): string[] {
  return Array.from(
    new Set(
      rows
        .filter(
          (row) =>
            row.simulation_output_id &&
            selectedKeys.has(row.simulation_output_id),
        )
        .map((row) => row.run_id),
    ),
  )
}

export function buildConversationsReportSelection(
  selectedKeys: Set<string>,
  runIdsBySimulationId: Record<string, string>,
): { runIds: string[]; simulationOutputIds: string[] } | null {
  const simulationOutputIds = Array.from(selectedKeys)
  if (simulationOutputIds.length === 0) {
    return null
  }

  const selectedRunIds = simulationOutputIds.flatMap((simulationOutputId) =>
    runIdsBySimulationId[simulationOutputId]
      ? [runIdsBySimulationId[simulationOutputId]]
      : [],
  )
  if (selectedRunIds.length !== simulationOutputIds.length) {
    return null
  }

  return {
    runIds: Array.from(new Set(selectedRunIds)),
    simulationOutputIds,
  }
}

interface ConversationsTableViewParams {
  // True once the first keyset fetch of this mount has settled.
  hasSettledInitialFetch: boolean
  // True when the keyset fetch failed, so zero rows means "could not load"
  // rather than "this organization has no conversations".
  isError: boolean
  isLoading: boolean
  rowCount: number
}

/**
 * Which surface the conversations table shows for the current fetch state.
 *
 * The organization record and the URL-synced filters both resolve after mount,
 * so the keyset query stays disabled across the first renders. React Query
 * reports a disabled query as neither loading nor fetching, so an empty state
 * keyed on `isLoading` alone would render before the request even goes out.
 * Only a settled first fetch proves the table is really empty.
 *
 * A failed fetch settles too, so without the error check a rejected first page
 * would fall through to the "upload your first conversation" call to action.
 * Rows that already loaded outrank a later page failing.
 */
export function getConversationsTableView({
  hasSettledInitialFetch,
  isError,
  isLoading,
  rowCount,
}: ConversationsTableViewParams): "loading" | "empty" | "error" | "rows" {
  if (isError && rowCount === 0) {
    return "error"
  }
  if (isLoading || !hasSettledInitialFetch) {
    return "loading"
  }
  return rowCount === 0 ? "empty" : "rows"
}
